import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Globe, Music, Users, Award, MapPin, Mic, GraduationCap, Calendar } from 'lucide-react';
import Nav from '../components/Nav';

const PressKitPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <Nav />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-8 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center animate-fade-in">
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-4 sm:mb-6 gradient-text px-2">Electronic Press Kit</h1>
          <p className="text-xl sm:text-2xl text-gray-300 mb-6 sm:mb-8">Dionlovelle</p>
          <p className="text-base sm:text-lg text-gray-400 max-w-3xl mx-auto px-4">
            Soul/R&amp;B singer-songwriter · Acoustic guitar · Live looping · Los Angeles
          </p>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a href="mailto:dionlovellebooking@gmail.com" className="glass-card p-6 hover:bg-purple-500/10 transition-all">
              <Mail className="mb-3 text-purple-400" size={32} />
              <h3 className="font-bold text-lg mb-2">Email</h3>
              <p className="text-gray-400 break-all">dionlovellebooking@gmail.com</p>
            </a>
            <a href="tel:209-424-9048" className="glass-card p-6 hover:bg-purple-500/10 transition-all">
              <Phone className="mb-3 text-purple-400" size={32} />
              <h3 className="font-bold text-lg mb-2">Phone</h3>
              <p className="text-gray-400">209-424-9048</p>
            </a>
            <Link to="/booking" className="glass-card p-6 hover:bg-purple-500/10 transition-all">
              <Globe className="mb-3 text-purple-400" size={32} />
              <h3 className="font-bold text-lg mb-2">Booking</h3>
              <p className="text-gray-400">View Booking Options</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Artist Bio */}
      <section className="py-12 px-6 bg-gradient-to-b from-black via-purple-900/10 to-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 gradient-text text-center" data-testid="about-heading">About Dionlovelle</h2>
          <div className="glass-card p-8" data-testid="about-content">
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Dionlovelle is a Soul/R&amp;B singer-songwriter and acoustic guitarist known for an intimate, dynamic sound that blends soulful vocals, expressive fingerstyle guitar, and live looping. His music centers on honest love songs and emotionally direct storytelling, delivered with a voice that moves effortlessly from soft, airy intimacy to powerful, soaring highs.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              Performing solo, Dionlovelle builds full arrangements in real time with guitar and looper, creating an immersive sound that feels both raw and expansive. Whether performing original music or thoughtfully selected covers, he brings vulnerability, musicianship, and intensity to every performance, aiming to make every listener feel something deeply personal.
            </p>
          </div>
        </div>
      </section>

      {/* Key Info Grid */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="glass-card p-6">
              <MapPin className="mb-3 text-purple-400" size={32} />
              <h3 className="font-bold text-xl mb-3">Location</h3>
              <p className="text-gray-300">Los Angeles, CA</p>
            </div>
            
            <div className="glass-card p-6">
              <Music className="mb-3 text-purple-400" size={32} />
              <h3 className="font-bold text-xl mb-3">Musical Style</h3>
              <p className="text-gray-300">Soul • R&B • Contemporary Pop • Acoustic / Singer-Songwriter</p>
            </div>
            
            <div className="glass-card p-6">
              <Mic className="mb-3 text-purple-400" size={32} />
              <h3 className="font-bold text-xl mb-3">Performance Format</h3>
              <p className="text-gray-300">Solo performance • Acoustic Guitar • Optional backing tracks</p>
            </div>
            
            <div className="glass-card p-6">
              <Calendar className="mb-3 text-purple-400" size={32} />
              <h3 className="font-bold text-xl mb-3">Availability</h3>
              <p className="text-gray-300">Weekday & Weekend • Private Events • College Shows</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Metrics */}
      <section className="py-12 px-6 bg-gradient-to-b from-black via-purple-900/10 to-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 gradient-text text-center">Audience & Reach</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="glass-card p-6 text-center">
              <Users className="mx-auto mb-3 text-purple-400" size={40} />
              <p className="text-3xl font-bold gradient-text">13,474</p>
              <p className="text-gray-400 mt-2">Twitch Followers</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Users className="mx-auto mb-3 text-purple-400" size={40} />
              <p className="text-3xl font-bold gradient-text">7,424</p>
              <p className="text-gray-400 mt-2">Instagram Followers</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Users className="mx-auto mb-3 text-purple-400" size={40} />
              <p className="text-3xl font-bold gradient-text">1,200</p>
              <p className="text-gray-400 mt-2">YouTube Subscribers</p>
            </div>
            <div className="glass-card p-6 text-center">
              <Users className="mx-auto mb-3 text-purple-400" size={40} />
              <p className="text-3xl font-bold gradient-text">1,134</p>
              <p className="text-gray-400 mt-2">Monthly Listeners</p>
            </div>
          </div>
          <div className="text-center mt-6 glass-card p-4">
            <p className="text-lg"><strong>Notable:</strong> 50,000+ views on most popular video • 230,000 stream views on Twitch</p>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 gradient-text text-center">Notable Achievements</h2>
          <div className="space-y-4">
            <div className="glass-card p-6 flex items-start gap-4">
              <Award className="text-yellow-400 flex-shrink-0" size={32} />
              <div>
                <h3 className="font-bold text-lg mb-2">Featured in LA Times (2022)</h3>
                <p className="text-gray-400">Press coverage highlighting artistic vision and community impact</p>
              </div>
            </div>
            <div className="glass-card p-6 flex items-start gap-4">
              <Award className="text-yellow-400 flex-shrink-0" size={32} />
              <div>
                <h3 className="font-bold text-lg mb-2">Pershing Square / Latin Festival (2022)</h3>
                <p className="text-gray-400">Live performance at major Los Angeles cultural event</p>
              </div>
            </div>
            <div className="glass-card p-6 flex items-start gap-4">
              <Award className="text-yellow-400 flex-shrink-0" size={32} />
              <div>
                <h3 className="font-bold text-lg mb-2">Warner Music Collaboration (2023)</h3>
                <p className="text-gray-400">Featured in Warner Music Live Stream program</p>
              </div>
            </div>
            <div className="glass-card p-6 flex items-start gap-4">
              <Award className="text-yellow-400 flex-shrink-0" size={32} />
              <div>
                <h3 className="font-bold text-lg mb-2">230,000 Stream Views (2024)</h3>
                <p className="text-gray-400">Reached major milestone on Twitch streaming platform</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vocal Coaching */}
      <section className="py-12 px-6 bg-gradient-to-b from-black via-purple-900/10 to-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 gradient-text text-center">Vocal Unlocked: Workshops & Masterclasses</h2>
          <div className="glass-card p-8 mb-8">
            <GraduationCap className="mb-4 text-purple-400" size={48} />
            <p className="text-lg text-gray-300 mb-6">
              Dionlovelle offers institutional programs for colleges and educational settings:
            </p>
            <ul className="space-y-3 text-gray-300 mb-6">
              <li className="flex items-start gap-2">
                <span className="text-purple-400">•</span>
                <span>Vocal Masterclasses (Technique, range, tone control)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-400">•</span>
                <span>Artist Development Workshops (Stage presence, identity, performance)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-400">•</span>
                <span>Songwriting / Expression Sessions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-400">•</span>
                <span>One-on-One Student Vocal Coaching</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-400">•</span>
                <span>Performance Prep for showcases, college events, and talent programs</span>
              </li>
            </ul>
            <div className="bg-purple-500/10 p-4 rounded-lg">
              <p className="font-semibold mb-2">Workshop Format Options:</p>
              <p className="text-gray-300">45–90 minute sessions • Full 2–3 hour masterclass • Multi-session series • Virtual or hybrid options</p>
            </div>
          </div>
          <div className="text-center">
            <a href="https://vocal-unlocked.emergent.host" target="_blank" rel="noopener noreferrer" className="glow-button">
              Visit Vocal Unlocked (External Site)
            </a>
          </div>
        </div>
      </section>

      {/* Music & Links */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 gradient-text text-center">Music & Discography</h2>
          <div className="glass-card p-8 mb-6">
            <h3 className="font-bold text-xl mb-4">Highlight Tracks:</h3>
            <ul className="space-y-2 text-gray-300">
              <li>• All To Myself - Dionlovelle</li>
              <li>• Love in LA - Dionlovelle</li>
            </ul>
            <div className="mt-6 flex flex-col sm:flex-row gap-4 flex-wrap">
              <a href="https://open.spotify.com/album/0fl1xqfuN53rQMdwOP3vbE" target="_blank" rel="noopener noreferrer" className="glow-button inline-flex items-center justify-center gap-2" data-testid="presskit-all-to-myself-link">
                <Music size={20} />
                All To Myself on Spotify
              </a>
              <a href="https://open.spotify.com/album/0vVJDGSzVAoy8DhSlnyYYl" target="_blank" rel="noopener noreferrer" className="glow-button inline-flex items-center justify-center gap-2" data-testid="presskit-love-in-la-link">
                <Music size={20} />
                Love in LA on Spotify
              </a>
              <a href="https://open.spotify.com/artist/2WUPin61rUCoiVp4rQxiVq" target="_blank" rel="noopener noreferrer" className="glass-card px-6 py-3 text-center hover:bg-green-500/10 transition-all inline-flex items-center justify-center gap-2" data-testid="presskit-spotify-artist-link">
                <Music size={18} />
                All Music on Spotify
              </a>
            </div>
          </div>
          
          <h3 className="font-bold text-2xl mb-4 text-center">Connect</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <a href="https://www.youtube.com/dionlovelle" target="_blank" rel="noopener noreferrer" className="glass-card p-4 text-center hover:bg-red-500/10 transition-all">
              <p className="font-semibold">YouTube</p>
            </a>
            <a href="https://www.instagram.com/dionlovelle" target="_blank" rel="noopener noreferrer" className="glass-card p-4 text-center hover:bg-pink-500/10 transition-all">
              <p className="font-semibold">Instagram</p>
            </a>
            <a href="https://www.twitch.tv/dionlovellej" target="_blank" rel="noopener noreferrer" className="glass-card p-4 text-center hover:bg-purple-500/10 transition-all">
              <p className="font-semibold">Twitch</p>
            </a>
            <a href="https://www.tiktok.com/@dionlovelle" target="_blank" rel="noopener noreferrer" className="glass-card p-4 text-center hover:bg-black/10 transition-all">
              <p className="font-semibold">TikTok</p>
            </a>
            <Link to="/booking" className="glass-card p-4 text-center hover:bg-gold-500/10 transition-all">
              <p className="font-semibold">Booking</p>
            </Link>
            <a href="https://vocal-unlocked.emergent.host" target="_blank" rel="noopener noreferrer" className="glass-card p-4 text-center hover:bg-blue-500/10 transition-all">
              <p className="font-semibold">Vocal Coaching (External)</p>
            </a>
          </div>
        </div>
      </section>

      {/* Technical Rider */}
      <section className="py-12 px-6 bg-gradient-to-b from-black via-purple-900/10 to-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 gradient-text text-center">Technical Rider</h2>
          <div className="glass-card p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-lg mb-3 text-purple-400">Hospitality</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Light catering (water, tea, snacks)</li>
                  <li>• Dressing room or private warm-up area</li>
                  <li>• Reliable internet connection</li>
                  <li>• Parking or load-in space</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-3 text-purple-400">Audio Requirements</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• PA System with 2 vocal mics (Shure SM58)</li>
                  <li>• 1 wedge monitor (or in-ear setup)</li>
                  <li>• Backing tracks via laptop</li>
                  <li>• Optional: Live keys or backing band</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-3 text-purple-400">Stage Setup</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Minimum 10ft × 10ft stage space</li>
                  <li>• Basic spotlight / ambient lighting</li>
                  <li>• Mood lighting preferred</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-3 text-purple-400">Performance Details</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Ideal for dinner service & lounges</li>
                  <li>• Perfect for intimate venues</li>
                  <li>• Available for colleges & museums</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center glass-card p-12">
          <h2 className="text-4xl font-bold mb-6 gradient-text">Ready to Book?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Perfect for luxury venues, colleges, museums, and intimate performances
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/booking" className="glow-button text-lg">
              View Booking Options
            </Link>
            <a href="mailto:dionlovellebooking@gmail.com" className="glow-button text-lg">
              <Mail className="inline mr-2" size={20} />
              Contact via Email
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400">© 2025 Dionlovelle. All rights reserved.</p>
          <p className="text-sm text-gray-500 mt-2">dionlovellebooking@gmail.com • 209-424-9048</p>
        </div>
      </footer>
    </div>
  );
};

export default PressKitPage;
